import type { AppView } from './navigation'
import type { CartItem, Product } from './types'
import { getAuthenticatedEntidadeId } from './authenticated-entidade'
import type {
  AiSearchResult,
  LojaVirtualAnalyticsBatchRequest,
  LojaVirtualAnalyticsEventInput,
  LojaVirtualAnalyticsStatus,
} from './api'

const VISITOR_ID_STORAGE_KEY = 'loja_virtual_analytics_visitor_id'
const SESSION_ID_STORAGE_KEY = 'loja_virtual_analytics_session_id'
const SESSION_LAST_SEEN_STORAGE_KEY = 'loja_virtual_analytics_session_last_seen_at'
const SESSION_LANDING_PATH_STORAGE_KEY = 'loja_virtual_analytics_landing_path'
const SESSION_ATTRIBUTION_STORAGE_KEY = 'loja_virtual_analytics_attribution'
const SESSION_ATTRIBUTION_COOKIE_NAME = 'loja_virtual_analytics_attribution'
const COOKIE_CONSENT_STORAGE_KEY = 'cookie-consent'
const CART_INSTANCE_ID_STORAGE_KEY = 'loja_virtual_analytics_cart_instance_id'
const CRITICAL_ANALYTICS_QUEUE_STORAGE_KEY = 'loja_virtual_analytics_critical_queue_v1'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000
const ANALYTICS_FLUSH_DELAY_MS = 1_500
const ANALYTICS_RETRY_DELAY_MS = 15_000
const ANALYTICS_BATCH_SIZE = 20
const CRITICAL_ANALYTICS_QUEUE_MAX_SIZE = 100

export const STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT = 'storefront-analytics:locationchange'

type StorefrontAttribution = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
}

export type StorefrontAnalyticsEventType =
  | 'page_view'
  | 'important_click'
  | 'search'
  | 'add_to_cart'
  | 'begin_checkout'
  | 'purchase'
  | 'lead_submit'
  | 'search_result_click'
  | 'cart_state'

export type StorefrontSearchMode = 'text' | 'image' | 'intelligent'

export type StorefrontSearchAnalyticsContext = {
  searchId: string
  searchMode: StorefrontSearchMode
  searchTerm?: string
  sourceView?: string
  resultPageByProductId?: Map<string, number>
}

export type StorefrontSearchResultContext = StorefrontSearchAnalyticsContext & {
  produtoId: number
  position: number
  page: number
  origin: string
}

export type StorefrontCheckoutAnalyticsContext = StorefrontAttribution & {
  visitor_id?: string
  session_id?: string
  cart_instance_id?: string
  referrer_url?: string
  referrer_host?: string
}

export type StorefrontAnalyticsPageKind =
  | 'home'
  | 'landing'
  | 'category'
  | 'product'
  | 'blog'
  | 'search'
  | 'checkout'
  | 'institutional'
  | 'other'

export type StorefrontAnalyticsEventOptions = {
  eventType: StorefrontAnalyticsEventType
  eventKey?: string
  pageKind?: StorefrontAnalyticsPageKind
  pageKey?: string
  pagePath?: string
  deviceType?: 'desktop' | 'mobile'
  entidadeId?: number | null
  produtoId?: number | null
  referrerUrl?: string | null
  referrerHost?: string | null
  landingPath?: string | null
  occurredAt?: string
  clientEventId?: string
  payload?: Record<string, unknown>
}

export type StorefrontPageViewContext = {
  currentView: AppView
  pathname: string
  search: string
  routeProductId?: string | null
  routeSupportSubjectSlug?: string | null
  checkoutStep?: string | null
  categoryRouteSlugs?: string[]
  isSearchResultsActive?: boolean
  searchQuery?: string
  selectedProduct?: Pick<Product, 'id' | 'name' | 'brand' | 'category' | 'subcategory' | 'origem'> | null
}

type StorefrontAiSearchAnalyticsInput = {
  message: string
  hasImage: boolean
  result: AiSearchResult
  products: Array<Pick<Product, 'id' | 'name'>>
  searchId?: string | null
}

type StorefrontAiSearchFailureAnalyticsInput = Pick<StorefrontAiSearchAnalyticsInput, 'message' | 'hasImage' | 'searchId'>

const AI_SEARCH_ANALYTICS_MAX_RESULTS = 18
const AI_SEARCH_ANALYTICS_MAX_TERMS = 20
const AI_SEARCH_ANALYTICS_MAX_TERM_LENGTH = 160
const AI_SEARCH_ANALYTICS_MAX_PRODUCT_NAME_LENGTH = 240
const AI_SEARCH_ANALYTICS_MAX_OBSERVATION_LENGTH = 500

export function buildStorefrontAiSearchAnalyticsPayload({
  message,
  hasImage,
  result,
  products,
  searchId,
}: StorefrontAiSearchAnalyticsInput): Record<string, unknown> {
  const normalizedMessage = message.trim().slice(0, 500)
  const productNamesById = new Map(
    products.map((product) => [
      String(product.id),
      String(product.name ?? '').trim().slice(0, AI_SEARCH_ANALYTICS_MAX_PRODUCT_NAME_LENGTH),
    ])
  )
  const productIds = products
    .slice(0, AI_SEARCH_ANALYTICS_MAX_RESULTS)
    .map((product) => Number.parseInt(String(product.id), 10))
    .filter((id) => Number.isFinite(id) && id > 0)
  const interpretedTerms = result.termos
    .slice(0, AI_SEARCH_ANALYTICS_MAX_TERMS)
    .map((term) => String(term ?? '').trim().slice(0, AI_SEARCH_ANALYTICS_MAX_TERM_LENGTH))
    .filter(Boolean)

  const normalizedSearchId = String(searchId ?? '').trim()

  return {
    search_term: normalizedMessage || 'Busca por imagem',
    results_count: productIds.length,
    source_view: 'ai_search',
    search_mode: 'intelligent',
    search_status: 'success',
    input_type: normalizedMessage ? (hasImage ? 'text_and_image' : 'text') : 'image',
    has_image: hasImage,
    interpreted_terms: interpretedTerms,
    ...(normalizedSearchId
      ? {
          search_id: normalizedSearchId,
          page: 1,
          page_results_count: productIds.length,
        }
      : {}),
    result_products: productIds.map((productId, index) => ({
      product_id: productId,
      ...(normalizedSearchId
        ? {
            produto_id: productId,
            position: index + 1,
            origin: 'flexi',
          }
        : {}),
      product_name: productNamesById.get(String(productId)) || '',
    })),
    observation: result.observacao?.trim().slice(0, AI_SEARCH_ANALYTICS_MAX_OBSERVATION_LENGTH) || undefined,
  }
}

export function buildStorefrontAiSearchFailureAnalyticsPayload({
  message,
  hasImage,
  searchId,
}: StorefrontAiSearchFailureAnalyticsInput): Record<string, unknown> {
  const normalizedMessage = message.trim().slice(0, 500)
  const normalizedSearchId = String(searchId ?? '').trim()
  return {
    search_term: normalizedMessage || 'Busca por imagem',
    source_view: 'ai_search',
    search_mode: 'intelligent',
    search_status: 'error',
    input_type: normalizedMessage ? (hasImage ? 'text_and_image' : 'text') : 'image',
    has_image: hasImage,
    ...(normalizedSearchId ? { search_id: normalizedSearchId, page: 1 } : {}),
  }
}

let historyInstrumentationApplied = false
let flushTimeoutId: number | null = null
let flushInFlightPromise: Promise<void> | null = null
let queuedEvents: LojaVirtualAnalyticsEventInput[] = []
let hydratedCriticalQueueKey: string | null = null
let analyticsDisabledWarningShown = false
let analyticsStatusSnapshot: LojaVirtualAnalyticsStatus = {
  enabled: true,
  reason: null,
  flagEnabled: true,
  integrationAuthConfigured: true,
}

declare global {
  interface Window {
    __lojaVirtualAnalytics?: {
      status: () => LojaVirtualAnalyticsStatus
      pending: () => number
      flush: (keepalive?: boolean) => Promise<void> | undefined
      location: () => string
    }
  }
}

function hasWindow() {
  return typeof window !== 'undefined'
}

function syncAnalyticsDebugHandle() {
  if (!hasWindow()) return

  try {
    window.__lojaVirtualAnalytics = {
      status: () => analyticsStatusSnapshot,
      pending: () => queuedEvents.length,
      flush: (keepalive = false) => flushStorefrontAnalyticsQueue({ keepalive }),
      location: () => getCurrentPagePath(),
    }
  } catch {
    // Analytics debug helpers must never interfere with storefront interactions.
  }
}

function normalizeNumericId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.trunc(parsed)
    }
  }

  return undefined
}

function generateAnalyticsId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function safeGetStorageValue(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function safeSetStorageValue(storage: Storage, key: string, value: string) {
  try {
    storage.setItem(key, value)
  } catch {
    // Ignore storage write errors.
  }
}

function safeRemoveStorageValue(storage: Storage, key: string) {
  try {
    storage.removeItem(key)
  } catch {
    // Ignore storage write errors.
  }
}

export function hasStorefrontGranularAnalyticsConsent() {
  if (!hasWindow()) return false

  try {
    const rawValue = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)
    if (!rawValue) return false
    if (rawValue === 'accepted') return true
    return JSON.parse(rawValue) === 'accepted'
  } catch {
    return false
  }
}

function resolveCriticalAnalyticsQueueKey() {
  const scope = getAuthenticatedEntidadeId() ?? 'guest'
  return `${CRITICAL_ANALYTICS_QUEUE_STORAGE_KEY}:${scope}`
}

function isCriticalCartAnalyticsEvent(event: LojaVirtualAnalyticsEventInput) {
  if (
    event.event_type !== 'cart_state'
    && event.event_type !== 'begin_checkout'
    && event.event_type !== 'purchase'
  ) return false
  const payload = event.payload && typeof event.payload === 'object'
    ? event.payload as Record<string, unknown>
    : null
  return Boolean(String(payload?.cart_instance_id ?? '').trim())
}

function readPersistentCriticalAnalyticsEvents(key: string): LojaVirtualAnalyticsEventInput[] {
  if (!hasWindow()) return []
  try {
    const parsed = JSON.parse(safeGetStorageValue(window.localStorage, key) || '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter((event): event is LojaVirtualAnalyticsEventInput => (
      Boolean(event)
      && typeof event === 'object'
      && typeof event.client_event_id === 'string'
      && isCriticalCartAnalyticsEvent(event as LojaVirtualAnalyticsEventInput)
    )).slice(-CRITICAL_ANALYTICS_QUEUE_MAX_SIZE)
  } catch {
    return []
  }
}

function writePersistentCriticalAnalyticsEvents(key: string, events: LojaVirtualAnalyticsEventInput[]) {
  if (!hasWindow()) return
  const uniqueEvents = new Map<string, LojaVirtualAnalyticsEventInput>()
  for (const event of events) {
    const clientEventId = String(event.client_event_id ?? '').trim()
    if (!clientEventId || !isCriticalCartAnalyticsEvent(event)) continue
    uniqueEvents.set(clientEventId, event)
  }
  const limited = [...uniqueEvents.values()].slice(-CRITICAL_ANALYTICS_QUEUE_MAX_SIZE)
  if (limited.length === 0) {
    safeRemoveStorageValue(window.localStorage, key)
    return
  }
  safeSetStorageValue(window.localStorage, key, JSON.stringify(limited))
}

function hydratePersistentCriticalAnalyticsEvents() {
  if (!hasWindow()) return
  const key = resolveCriticalAnalyticsQueueKey()
  if (!hasStorefrontGranularAnalyticsConsent()) {
    safeRemoveStorageValue(window.localStorage, key)
    hydratedCriticalQueueKey = key
    return
  }
  if (hydratedCriticalQueueKey === key) return
  hydratedCriticalQueueKey = key
  const queuedIds = new Set(queuedEvents.map((event) => String(event.client_event_id ?? '')))
  const persisted = readPersistentCriticalAnalyticsEvents(key)
    .filter((event) => !queuedIds.has(String(event.client_event_id ?? '')))
  if (persisted.length > 0) queuedEvents = [...persisted, ...queuedEvents]
}

function persistCriticalAnalyticsEvent(event: LojaVirtualAnalyticsEventInput) {
  if (!hasWindow() || !isCriticalCartAnalyticsEvent(event)) return
  const key = resolveCriticalAnalyticsQueueKey()
  const persisted = readPersistentCriticalAnalyticsEvents(key)
  writePersistentCriticalAnalyticsEvents(key, [...persisted, event])
  hydratedCriticalQueueKey = key
}

function removeDeliveredCriticalAnalyticsEvents(
  batch: LojaVirtualAnalyticsEventInput[],
  key = resolveCriticalAnalyticsQueueKey(),
) {
  if (!hasWindow()) return
  const deliveredIds = new Set(
    batch.filter(isCriticalCartAnalyticsEvent).map((event) => String(event.client_event_id ?? '')),
  )
  if (deliveredIds.size === 0) return
  writePersistentCriticalAnalyticsEvents(
    key,
    readPersistentCriticalAnalyticsEvents(key).filter((event) => (
      !deliveredIds.has(String(event.client_event_id ?? ''))
    )),
  )
}

export function createStorefrontSearchId() {
  return generateAnalyticsId('search')
}

function normalizeSearchResultOrigin(product: Pick<Product, 'origem'>) {
  return product.origem === 'marketplace_vitrine' ? 'marketplace_vitrine' : 'flexi'
}

export function buildStorefrontSearchResultProducts(
  products: Array<Pick<Product, 'id' | 'origem'>>,
  options?: { startPosition?: number; positionByProductId?: ReadonlyMap<string, number> }
) {
  const startPosition = Math.max(1, Math.trunc(options?.startPosition ?? 1))

  return products.flatMap((product, index) => {
    const produtoId = normalizeNumericId(product.id)
    if (!produtoId) return []

    return [{
      produto_id: produtoId,
      position: Math.max(
        1,
        Math.trunc(options?.positionByProductId?.get(String(product.id)) ?? (startPosition + index)),
      ),
      origin: normalizeSearchResultOrigin(product),
    }]
  })
}

export function buildStorefrontSearchResultContext(
  context: StorefrontSearchAnalyticsContext,
  product: Pick<Product, 'id' | 'origem'>,
  options: { position: number; page?: number }
): StorefrontSearchResultContext | null {
  const produtoId = normalizeNumericId(product.id)
  const searchId = context.searchId.trim()
  if (!produtoId || !searchId) return null

  const resultPage = options.page ?? context.resultPageByProductId?.get(String(product.id)) ?? 1

  return {
    ...context,
    searchId,
    produtoId,
    position: Math.max(1, Math.trunc(options.position)),
    page: Math.max(1, Math.trunc(resultPage)),
    origin: normalizeSearchResultOrigin(product),
  }
}

export function registerStorefrontSearchResultPage(
  context: StorefrontSearchAnalyticsContext,
  products: Array<Pick<Product, 'id'>>,
  page: number
) {
  const resultPage = Math.max(1, Math.trunc(page))
  const pagesByProductId = context.resultPageByProductId ?? new Map<string, number>()
  context.resultPageByProductId = pagesByProductId

  products.forEach((product) => {
    const productId = String(product.id ?? '').trim()
    if (productId && !pagesByProductId.has(productId)) {
      pagesByProductId.set(productId, resultPage)
    }
  })
}

export function withStorefrontGranularAnalyticsPayload(
  currentPayload: Record<string, unknown>,
  granularPayload: Record<string, unknown>
) {
  return hasStorefrontGranularAnalyticsConsent()
    ? { ...currentPayload, ...granularPayload }
    : currentPayload
}

export function withStorefrontSearchResultAttribution(
  currentPayload: Record<string, unknown>,
  context?: StorefrontSearchResultContext | null
) {
  if (!context) return currentPayload

  return withStorefrontGranularAnalyticsPayload(currentPayload, {
    search_id: context.searchId,
    search_mode: context.searchMode,
    search_term: context.searchTerm || undefined,
    position: context.position,
    page: context.page,
    origin: context.origin,
  })
}

function safeGetCookieValue(name: string): string | null {
  if (!hasWindow()) return null

  try {
    const cookies = document.cookie ? document.cookie.split(';') : []
    const cookiePrefix = `${encodeURIComponent(name)}=`
    const match = cookies.find((entry) => entry.trim().startsWith(cookiePrefix))
    if (!match) return null
    return decodeURIComponent(match.trim().slice(cookiePrefix.length))
  } catch {
    return null
  }
}

function safeSetSessionCookie(name: string, value: string) {
  if (!hasWindow()) return

  try {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; SameSite=Lax`
  } catch {
    // Ignore cookie write errors.
  }
}

function getCurrentPagePath() {
  if (!hasWindow()) return '/'
  const pathname = window.location.pathname || '/'
  const search = window.location.search || ''
  return `${pathname}${search}`
}

function getCurrentPageUrl() {
  if (!hasWindow()) return ''
  return window.location.href
}

function buildStoredAttribution(search: string): StorefrontAttribution {
  const params = new URLSearchParams(search)
  const attribution: StorefrontAttribution = {}

  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'] as const
  keys.forEach((key) => {
    const value = (params.get(key) || '').trim()
    if (value) {
      attribution[key] = value
    }
  })

  return attribution
}

function hasAttribution(attribution: StorefrontAttribution) {
  return Object.values(attribution).some(Boolean)
}

function parseStoredAttribution(source: string | null): StorefrontAttribution | null {
  if (!source) return null

  try {
    const parsed = JSON.parse(source) as StorefrontAttribution
    if (parsed && typeof parsed === 'object' && hasAttribution(parsed)) {
      return parsed
    }
  } catch {
    // Ignore malformed attribution payloads.
  }

  return null
}

function persistSessionAttribution(attribution: StorefrontAttribution) {
  if (!hasWindow() || !hasAttribution(attribution)) return

  const serialized = JSON.stringify(attribution)
  safeSetStorageValue(window.sessionStorage, SESSION_ATTRIBUTION_STORAGE_KEY, serialized)
  safeSetSessionCookie(SESSION_ATTRIBUTION_COOKIE_NAME, serialized)
}

function resolveVisitorId(): string | undefined {
  if (!hasWindow()) return undefined

  const existing = safeGetStorageValue(window.localStorage, VISITOR_ID_STORAGE_KEY)
  if (existing?.trim()) return existing.trim()

  const nextValue = generateAnalyticsId('visitor')
  safeSetStorageValue(window.localStorage, VISITOR_ID_STORAGE_KEY, nextValue)
  return nextValue
}

function resolveSessionId(): string | undefined {
  if (!hasWindow()) return undefined

  const now = Date.now()
  const existingSessionId = safeGetStorageValue(window.sessionStorage, SESSION_ID_STORAGE_KEY)?.trim()
  const lastSeenRaw = safeGetStorageValue(window.sessionStorage, SESSION_LAST_SEEN_STORAGE_KEY)
  const lastSeenAt = lastSeenRaw ? Number.parseInt(lastSeenRaw, 10) : Number.NaN
  const hasActiveSession = existingSessionId
    && Number.isFinite(lastSeenAt)
    && now - lastSeenAt < SESSION_TIMEOUT_MS

  const nextSessionId = hasActiveSession ? existingSessionId : generateAnalyticsId('session')

  if (nextSessionId) {
    safeSetStorageValue(window.sessionStorage, SESSION_ID_STORAGE_KEY, nextSessionId)
    safeSetStorageValue(window.sessionStorage, SESSION_LAST_SEEN_STORAGE_KEY, String(now))
  }

  return nextSessionId || undefined
}

export function getStorefrontAnalyticsIdentity() {
  if (!hasWindow()) {
    return { visitor_id: undefined, session_id: undefined, device_type: 'other' as const }
  }
  const mobile = /(iphone|ipod|ipad|android|mobile|windows phone|opera mini|blackberry|silk|kindle|tablet)/i
    .test(window.navigator.userAgent)
  return {
    visitor_id: resolveVisitorId(),
    session_id: resolveSessionId(),
    device_type: mobile ? 'mobile' as const : 'desktop' as const,
  }
}

function resolveLandingPath() {
  if (!hasWindow()) return undefined

  const existing = safeGetStorageValue(window.sessionStorage, SESSION_LANDING_PATH_STORAGE_KEY)?.trim()
  if (existing) return existing

  const nextValue = getCurrentPagePath()
  safeSetStorageValue(window.sessionStorage, SESSION_LANDING_PATH_STORAGE_KEY, nextValue)
  return nextValue
}

function resolveSessionAttribution(): StorefrontAttribution {
  if (!hasWindow()) return {}

  const storedSessionAttribution = parseStoredAttribution(
    safeGetStorageValue(window.sessionStorage, SESSION_ATTRIBUTION_STORAGE_KEY)
  )
  if (storedSessionAttribution) {
    if (!parseStoredAttribution(safeGetCookieValue(SESSION_ATTRIBUTION_COOKIE_NAME))) {
      persistSessionAttribution(storedSessionAttribution)
    }
    return storedSessionAttribution
  }

  const storedCookieAttribution = parseStoredAttribution(
    safeGetCookieValue(SESSION_ATTRIBUTION_COOKIE_NAME)
  )
  if (storedCookieAttribution) {
    persistSessionAttribution(storedCookieAttribution)
    return storedCookieAttribution
  }

  const nextValue = buildStoredAttribution(window.location.search || '')
  if (hasAttribution(nextValue)) {
    persistSessionAttribution(nextValue)
  }
  return nextValue
}

export function getStorefrontAttributionOrigin() {
  const attribution = resolveSessionAttribution()
  if (!hasAttribution(attribution)) return ''

  const params = new URLSearchParams()
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'] as const
  keys.forEach((key) => {
    const value = attribution[key]?.trim()
    if (value) {
      params.set(key, value)
    }
  })

  return params.toString()
}

function extractHostFromUrl(value?: string | null): string | undefined {
  if (!value?.trim()) return undefined

  try {
    return new URL(value).host || undefined
  } catch {
    return undefined
  }
}

function clearScheduledFlush() {
  if (flushTimeoutId === null || !hasWindow()) return
  window.clearTimeout(flushTimeoutId)
  flushTimeoutId = null
}

function scheduleAnalyticsFlush(delayMs = ANALYTICS_FLUSH_DELAY_MS) {
  if (!hasWindow()) return
  clearScheduledFlush()
  flushTimeoutId = window.setTimeout(() => {
    flushTimeoutId = null
    void flushStorefrontAnalyticsQueue()
  }, delayMs)
}

async function resolveAnalyticsDispatchConfig() {
  const api = await import('./api')
  const analyticsStatus = api.getLojaVirtualAnalyticsStatus()
  analyticsStatusSnapshot = analyticsStatus

  return {
    analyticsStatus,
    ingestLojaVirtualAnalyticsEvents: api.ingestLojaVirtualAnalyticsEvents,
    lojaId: analyticsStatus.lojaId ?? api.getLojaVirtualDefaultLojaId(),
  }
}

function buildSharedAnalyticsPayload(lojaId: number): Omit<LojaVirtualAnalyticsBatchRequest, 'events'> | null {
  if (!analyticsStatusSnapshot.enabled) {
    if (hasWindow() && !analyticsDisabledWarningShown) {
      console.warn('[loja-analytics] envio desabilitado', analyticsStatusSnapshot)
      analyticsDisabledWarningShown = true
    }
    return null
  }

  const attribution = resolveSessionAttribution()
  const entidadeId = getAuthenticatedEntidadeId()

  return {
    loja_id: lojaId,
    ...(typeof entidadeId === 'number' ? { entidade_id: entidadeId } : {}),
    ...(resolveVisitorId() ? { visitor_id: resolveVisitorId() } : {}),
    ...(resolveSessionId() ? { session_id: resolveSessionId() } : {}),
    ...(resolveLandingPath() ? { landing_path: resolveLandingPath() } : {}),
    ...attribution,
  }
}

function dispatchLocationChangeEvent() {
  if (!hasWindow()) return
  window.dispatchEvent(new Event(STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT))
}

export function ensureStorefrontAnalyticsHistoryInstrumentation() {
  if (!hasWindow() || historyInstrumentationApplied) return

  syncAnalyticsDebugHandle()

  const history = window.history
  ;(['pushState', 'replaceState'] as const).forEach((methodName) => {
    const originalMethod = history[methodName]
    history[methodName] = function patchedHistoryMethod(...args: Parameters<History[typeof methodName]>) {
      const result = originalMethod.apply(this, args)
      resolveSessionId()
      resolveLandingPath()
      resolveSessionAttribution()
      dispatchLocationChangeEvent()
      return result
    }
  })

  historyInstrumentationApplied = true
}

export function getStorefrontAnalyticsLocationKey() {
  return getCurrentPagePath()
}

export function queueStorefrontAnalyticsEvent(options: StorefrontAnalyticsEventOptions) {
  syncAnalyticsDebugHandle()
  hydratePersistentCriticalAnalyticsEvents()
  const attribution = resolveSessionAttribution()
  const resolvedEntidadeId = normalizeNumericId(options.entidadeId) ?? getAuthenticatedEntidadeId()
  const resolvedProductId = normalizeNumericId(options.produtoId)
  const resolvedPagePath = (options.pagePath || getCurrentPagePath()).trim()
  const resolvedReferrerUrl = options.referrerUrl?.trim() || undefined
  const resolvedLandingPath = options.landingPath?.trim() || resolveLandingPath()
  const resolvedDeviceType = options.deviceType
  const payload = options.payload && Object.keys(options.payload).length > 0
    ? options.payload
    : undefined

  const event: LojaVirtualAnalyticsEventInput = {
    ...(resolveVisitorId() ? { visitor_id: resolveVisitorId() } : {}),
    ...(resolveSessionId() ? { session_id: resolveSessionId() } : {}),
    ...(typeof resolvedEntidadeId === 'number' ? { entidade_id: resolvedEntidadeId } : {}),
    ...(typeof resolvedProductId === 'number' ? { produto_id: resolvedProductId } : {}),
    ...(resolvedDeviceType ? { device_type: resolvedDeviceType } : {}),
    event_type: options.eventType,
    ...(options.eventKey ? { event_key: options.eventKey } : {}),
    ...(options.pageKind ? { page_kind: options.pageKind } : {}),
    ...(options.pageKey ? { page_key: options.pageKey } : {}),
    ...(resolvedPagePath ? { page_path: resolvedPagePath } : {}),
    ...(options.clientEventId ? { client_event_id: options.clientEventId } : { client_event_id: generateAnalyticsId('evt') }),
    occurred_at: options.occurredAt || new Date().toISOString(),
    ...(resolvedReferrerUrl ? { referrer_url: resolvedReferrerUrl } : {}),
    ...(options.referrerHost?.trim()
      ? { referrer_host: options.referrerHost.trim() }
      : extractHostFromUrl(resolvedReferrerUrl)
        ? { referrer_host: extractHostFromUrl(resolvedReferrerUrl) }
        : {}),
    ...(resolvedLandingPath ? { landing_path: resolvedLandingPath } : {}),
    ...attribution,
    ...(payload ? { payload } : {}),
  }
  queuedEvents.push(event)
  persistCriticalAnalyticsEvent(event)

  if (queuedEvents.length >= ANALYTICS_BATCH_SIZE) {
    void flushStorefrontAnalyticsQueue()
    return
  }

  scheduleAnalyticsFlush()
}

export function trackStorefrontImportantClick(
  options: Omit<StorefrontAnalyticsEventOptions, 'eventType'> & { eventKey: string }
) {
  try {
    queueStorefrontAnalyticsEvent({
      ...options,
      eventType: 'important_click',
    })
  } catch (error) {
    console.warn('Falha ao registrar clique importante da Loja Virtual.', error)
  }
}

export function queueStorefrontConsentGrantedAnalyticsEvent(options: StorefrontAnalyticsEventOptions) {
  if (!hasStorefrontGranularAnalyticsConsent()) return false

  try {
    queueStorefrontAnalyticsEvent(options)
    return true
  } catch (error) {
    console.warn('Falha ao registrar evento consentido da Loja Virtual.', error)
    return false
  }
}

export function trackStorefrontSearchResultClick(
  context?: StorefrontSearchResultContext | null,
  action: 'open' | 'add_to_cart' = 'open'
) {
  if (!context) return false

  return queueStorefrontConsentGrantedAnalyticsEvent({
    eventType: 'search_result_click',
    eventKey: action,
    pageKind: 'search',
    pageKey: `search:${context.searchId}`,
    produtoId: context.produtoId,
    payload: {
      search_id: context.searchId,
      search_mode: context.searchMode,
      search_term: context.searchTerm || undefined,
      source_view: context.sourceView || undefined,
      position: context.position,
      page: context.page,
      origin: context.origin,
      action,
    },
  })
}

function resolveStoredCartInstanceId() {
  if (!hasWindow()) return undefined
  const scope = getAuthenticatedEntidadeId() ?? 'guest'
  const scopedKey = `${CART_INSTANCE_ID_STORAGE_KEY}:${scope}`
  const sharedValue = safeGetStorageValue(window.localStorage, scopedKey)?.trim()
  if (sharedValue) return sharedValue

  // Migra o identificador antigo da aba para uma chave compartilhada entre
  // abas/sessões. Assim o mesmo carrinho não vira abandono ao ser retomado.
  const legacyValue = safeGetStorageValue(window.sessionStorage, CART_INSTANCE_ID_STORAGE_KEY)?.trim()
  if (!legacyValue) return undefined
  safeSetStorageValue(window.localStorage, scopedKey, legacyValue)
  safeRemoveStorageValue(window.sessionStorage, CART_INSTANCE_ID_STORAGE_KEY)
  return legacyValue
}

function resolveCartInstanceStorageKey() {
  const scope = getAuthenticatedEntidadeId() ?? 'guest'
  return `${CART_INSTANCE_ID_STORAGE_KEY}:${scope}`
}

function resolveCartInstanceId(createIfMissing: boolean) {
  if (!hasWindow()) return undefined

  const existing = resolveStoredCartInstanceId()
  if (existing || !createIfMissing) return existing

  const nextValue = generateAnalyticsId('cart')
  safeSetStorageValue(window.localStorage, resolveCartInstanceStorageKey(), nextValue)
  return nextValue
}

export function buildStorefrontCartStateAnalyticsPayload(
  items: CartItem[],
  cartInstanceId: string,
  reason: string
) {
  const consolidatedItems = new Map<string, {
    produto_id: number
    quantidade: number
    produto_variacao_id?: number
  }>()

  for (const item of items) {
    const produtoId = normalizeNumericId(item.product.cartBaseProductId ?? item.product.id)
    const produtoVariacaoId = normalizeNumericId(item.product.id)
    const quantidade = Math.max(0, Math.trunc(Number(item.quantity) || 0))
    if (!produtoId || quantidade <= 0) continue

    const itemKey = `${produtoId}:${produtoVariacaoId ?? ''}`
    const current = consolidatedItems.get(itemKey)
    consolidatedItems.set(itemKey, {
      produto_id: produtoId,
      quantidade: (current?.quantidade ?? 0) + quantidade,
      ...(produtoVariacaoId && produtoVariacaoId !== produtoId
        ? { produto_variacao_id: produtoVariacaoId }
        : current?.produto_variacao_id
          ? { produto_variacao_id: current.produto_variacao_id }
          : {}),
    })
  }

  const normalizedItems = Array.from(consolidatedItems.values())
  return {
    cart_instance_id: cartInstanceId,
    reason,
    items_count: normalizedItems.length,
    quantity_total: normalizedItems.reduce((sum, item) => sum + item.quantidade, 0),
    items: normalizedItems,
  }
}

export function trackStorefrontCartState(items: CartItem[], reason: string) {
  if (!hasStorefrontGranularAnalyticsConsent()) return null

  try {
    const cartInstanceId = resolveCartInstanceId(items.length > 0)
    if (!cartInstanceId) return null

    queueStorefrontConsentGrantedAnalyticsEvent({
      eventType: 'cart_state',
      eventKey: reason,
      pageKind: 'checkout',
      payload: buildStorefrontCartStateAnalyticsPayload(items, cartInstanceId, reason),
    })

    if (items.length === 0 && hasWindow()) {
      safeRemoveStorageValue(window.localStorage, resolveCartInstanceStorageKey())
      safeRemoveStorageValue(window.sessionStorage, CART_INSTANCE_ID_STORAGE_KEY)
    }

    return cartInstanceId
  } catch (error) {
    console.warn('Falha ao registrar estado do carrinho da Loja Virtual.', error)
    return null
  }
}

export function getStorefrontCheckoutAnalyticsContext(
  options: { includeCart?: boolean } = {}
): StorefrontCheckoutAnalyticsContext | null {
  if (!hasStorefrontGranularAnalyticsConsent()) return null

  try {
    const attribution = resolveSessionAttribution()
    const referrerUrl = typeof document !== 'undefined' ? document.referrer?.trim() : ''
    const visitorId = resolveVisitorId()
    const sessionId = resolveSessionId()
    const cartInstanceId = options.includeCart === false
      ? undefined
      : resolveCartInstanceId(false)
    const referrerHost = extractHostFromUrl(referrerUrl)

    return {
      ...(visitorId ? { visitor_id: visitorId } : {}),
      ...(sessionId ? { session_id: sessionId } : {}),
      ...(cartInstanceId ? { cart_instance_id: cartInstanceId } : {}),
      ...(referrerUrl ? { referrer_url: referrerUrl } : {}),
      ...(referrerHost ? { referrer_host: referrerHost } : {}),
      ...attribution,
    }
  } catch {
    // Analytics is best-effort and must never prevent an order from being sent.
    return null
  }
}

export function withStorefrontCartAnalyticsPayload(
  currentPayload: Record<string, unknown>,
  items: CartItem[]
) {
  if (!hasStorefrontGranularAnalyticsConsent()) return currentPayload

  try {
    const cartInstanceId = resolveCartInstanceId(items.length > 0)
    if (!cartInstanceId) return currentPayload

    const cartPayload = buildStorefrontCartStateAnalyticsPayload(items, cartInstanceId, 'checkout_context')
    return {
      ...currentPayload,
      cart_instance_id: cartInstanceId,
      items: cartPayload.items,
    }
  } catch {
    return currentPayload
  }
}

export function trackStorefrontBeginCheckout(items: CartItem[], sourceView: string) {
  if (!hasStorefrontGranularAnalyticsConsent() || items.length === 0) return null

  try {
    const cartInstanceId = resolveCartInstanceId(true)
    if (!cartInstanceId) return null

    const cartPayload = buildStorefrontCartStateAnalyticsPayload(items, cartInstanceId, 'begin_checkout')
    queueStorefrontConsentGrantedAnalyticsEvent({
      eventType: 'begin_checkout',
      pageKind: 'checkout',
      pageKey: 'cart',
      payload: {
        ...cartPayload,
        source_view: sourceView,
      },
    })
    return cartInstanceId
  } catch (error) {
    console.warn('Falha ao registrar início do checkout da Loja Virtual.', error)
    return null
  }
}

export function trackStorefrontPurchase(
  items: CartItem[],
  order: { pedidoId?: number | null; numeroPedido?: number | null },
  sourceView: string
) {
  if (!hasStorefrontGranularAnalyticsConsent()) return false

  try {
    const cartInstanceId = resolveCartInstanceId(items.length > 0)
    const cartPayload = cartInstanceId
      ? buildStorefrontCartStateAnalyticsPayload(items, cartInstanceId, 'purchase')
      : null

    return queueStorefrontConsentGrantedAnalyticsEvent({
      eventType: 'purchase',
      pageKind: 'checkout',
      pageKey: 'success',
      payload: {
        pedido_id: order.pedidoId || undefined,
        numero_pedido: order.numeroPedido || undefined,
        items_count: items.length,
        quantity_total: items.reduce((sum, item) => sum + item.quantity, 0),
        source_view: sourceView,
        ...(cartPayload
          ? {
              cart_instance_id: cartInstanceId,
              items: cartPayload.items,
            }
          : {}),
      },
    })
  } catch (error) {
    console.warn('Falha ao registrar compra da Loja Virtual.', error)
    return false
  }
}

export async function flushStorefrontAnalyticsQueue(options?: { keepalive?: boolean }) {
  syncAnalyticsDebugHandle()
  hydratePersistentCriticalAnalyticsEvents()
  if (queuedEvents.length === 0) return
  if (flushInFlightPromise) return flushInFlightPromise

  const { analyticsStatus, ingestLojaVirtualAnalyticsEvents, lojaId } = await resolveAnalyticsDispatchConfig()
  const sharedPayload = typeof lojaId === 'number' ? buildSharedAnalyticsPayload(lojaId) : null
  if (!sharedPayload) {
    if (!analyticsStatus.enabled || typeof lojaId !== 'number') {
      analyticsDisabledWarningShown = true
    }
    queuedEvents = []
    clearScheduledFlush()
    return
  }

  clearScheduledFlush()

  const batch = queuedEvents.splice(0, ANALYTICS_BATCH_SIZE)
  const criticalQueueKey = hasWindow() ? resolveCriticalAnalyticsQueueKey() : ''
  let retryCriticalEvents = false
  flushInFlightPromise = ingestLojaVirtualAnalyticsEvents(
    {
      ...sharedPayload,
      events: batch,
    },
    {
      keepalive: options?.keepalive,
    }
  )
    .then(() => {
      if (criticalQueueKey) removeDeliveredCriticalAnalyticsEvents(batch, criticalQueueKey)
    })
    .catch((error) => {
      console.warn('Falha ao registrar eventos de analytics da Loja Virtual.', error)
      const criticalEvents = batch.filter(isCriticalCartAnalyticsEvent)
      if (criticalEvents.length > 0) {
        const queuedIds = new Set(queuedEvents.map((event) => String(event.client_event_id ?? '')))
        queuedEvents = [
          ...criticalEvents.filter((event) => !queuedIds.has(String(event.client_event_id ?? ''))),
          ...queuedEvents,
        ]
        retryCriticalEvents = true
      }
    })
    .finally(() => {
      flushInFlightPromise = null
      if (queuedEvents.length > 0) {
        scheduleAnalyticsFlush(retryCriticalEvents ? ANALYTICS_RETRY_DELAY_MS : ANALYTICS_FLUSH_DELAY_MS)
      }
    })

  return flushInFlightPromise
}

function buildBlogPagePayload(pathname: string) {
  const segments = pathname.split('/').filter(Boolean)
  const isBlogRoot = segments[0] === 'comunidade' || segments[0] === 'blog'
  if (!isBlogRoot) {
    return {
      pageKey: 'blog',
      payload: {
        page_subtype: 'home',
      },
    }
  }

  if (segments[1] === 'artigo' && segments[2]) {
    return {
      pageKey: `article:${segments[2]}`,
      payload: {
        page_subtype: 'article',
        article_slug: segments[2],
      },
    }
  }

  if (segments[1] === 'categoria' && segments[2]) {
    return {
      pageKey: `category:${segments[2]}`,
      payload: {
        page_subtype: 'category',
        category_slug: segments[2],
      },
    }
  }

  const subtype = segments[1] || 'home'
  return {
    pageKey: subtype,
    payload: {
      page_subtype: subtype,
    },
  }
}

export function buildStorefrontPageViewEvent(context: StorefrontPageViewContext): Omit<StorefrontAnalyticsEventOptions, 'eventType'> {
  const normalizedPathname = context.pathname || '/'
  const normalizedSearch = context.search || ''
  const pagePath = `${normalizedPathname}${normalizedSearch}`
  const normalizedSearchQuery = (context.searchQuery || '').trim()
  const productId = normalizeNumericId(context.selectedProduct?.id ?? context.routeProductId)

  switch (context.currentView) {
    case 'home':
      return {
        pageKind: 'home',
        pageKey: 'home',
        pagePath,
        payload: {
          page_subtype: 'home',
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'products':
      if (context.isSearchResultsActive && normalizedSearchQuery) {
        return {
          pageKind: 'search',
          pageKey: `search:${normalizedSearchQuery}`,
          pagePath,
          payload: {
            page_subtype: 'search',
            search_query: normalizedSearchQuery,
            title: typeof document !== 'undefined' ? document.title : undefined,
          },
        }
      }

      if (Array.isArray(context.categoryRouteSlugs) && context.categoryRouteSlugs.length > 0) {
        return {
          pageKind: 'category',
          pageKey: `category:${context.categoryRouteSlugs.join('/')}`,
          pagePath,
          payload: {
            page_subtype: 'category',
            category_slugs: context.categoryRouteSlugs,
            title: typeof document !== 'undefined' ? document.title : undefined,
          },
        }
      }

      return {
        pageKind: 'category',
        pageKey: 'catalog',
        pagePath,
        payload: {
          page_subtype: 'catalog',
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'launches':
      return {
        pageKind: 'landing',
        pageKey: 'launches',
        pagePath,
        payload: {
          page_subtype: 'launches',
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'just_arrived':
      return {
        pageKind: 'landing',
        pageKey: 'just_arrived',
        pagePath,
        payload: {
          page_subtype: 'just_arrived',
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'product-detail':
      return {
        pageKind: 'product',
        pageKey: String(productId ?? context.routeProductId ?? normalizedPathname),
        pagePath,
        produtoId: productId,
        payload: {
          page_subtype: 'detail',
          product_name: context.selectedProduct?.name || undefined,
          product_brand: context.selectedProduct?.brand || undefined,
          product_category: context.selectedProduct?.category || undefined,
          product_subcategory: context.selectedProduct?.subcategory || undefined,
          origin: context.selectedProduct
            ? normalizeSearchResultOrigin(context.selectedProduct)
            : undefined,
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'checkout':
      return {
        pageKind: 'checkout',
        pageKey: context.checkoutStep || 'cart',
        pagePath,
        payload: {
          page_subtype: context.checkoutStep || 'cart',
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'support':
      return {
        pageKind: 'other',
        pageKey: context.routeSupportSubjectSlug?.trim() || 'support',
        pagePath,
        payload: {
          page_subtype: context.routeSupportSubjectSlug?.trim() ? 'subject' : 'home',
          subject_slug: context.routeSupportSubjectSlug?.trim() || undefined,
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'tracking':
    case 'quotes':
    case 'dashboard':
    case 'profile':
    case 'finance':
    case 'benefits':
    case 'addresses':
    case 'favorites':
    case 'orders':
    case 'affiliates':
    case 'returns':
      return {
        pageKind: 'other',
        pageKey: context.currentView,
        pagePath,
        payload: {
          page_subtype: context.currentView,
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'return-request':
      return {
        pageKind: 'other',
        pageKey: 'return-request',
        pagePath,
        payload: {
          page_subtype: 'return-request',
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'login':
    case 'register':
      return {
        pageKind: 'other',
        pageKey: context.currentView,
        pagePath,
        payload: {
          page_subtype: context.currentView,
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'about':
    case 'privacy':
    case 'terms':
    case 'careers':
      return {
        pageKind: 'institutional',
        pageKey: context.currentView,
        pagePath,
        payload: {
          page_subtype: context.currentView,
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    case 'blog': {
      const blogPage = buildBlogPagePayload(normalizedPathname)
      return {
        pageKind: 'blog',
        pageKey: blogPage.pageKey,
        pagePath,
        payload: {
          ...blogPage.payload,
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    }
    case 'compare':
      return {
        pageKind: 'other',
        pageKey: 'compare',
        pagePath,
        payload: {
          page_subtype: 'compare',
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
    default:
      return {
        pageKind: 'other',
        pageKey: context.currentView || normalizedPathname,
        pagePath,
        payload: {
          page_subtype: context.currentView,
          title: typeof document !== 'undefined' ? document.title : undefined,
        },
      }
  }
}

export function buildStorefrontProductPath(product: Pick<Product, 'id' | 'name'>) {
  const slugBase = (product.name || '')
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'produto'

  return `/produto/${product.id}/${slugBase}`
}

export function getCurrentStorefrontUrl() {
  return getCurrentPageUrl()
}
